import { useRef, useState, useEffect, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { uploadImage, saveFromUrl } from '../../api/images';

export default function UploadZone({ folderId, categoryId }) {
  const qc       = useQueryClient();
  const inputRef = useRef(null);
  const [tab,           setTab]           = useState('file');
  const [dragging,      setDragging]      = useState(false);
  const [progress,      setProgress]      = useState(null);
  const [error,         setError]         = useState('');
  const [urlValue,      setUrlValue]      = useState('');
  const [urlName,       setUrlName]       = useState('');
  const [urlLoading,    setUrlLoading]    = useState(false);
  const [pasteStatus,   setPasteStatus]   = useState('idle'); // idle | reading | success | error
  const [pastePreview,  setPastePreview]  = useState(null);   // object-URL for preview

  const fileMutation = useMutation({
    mutationFn: (file) => {
      const form = new FormData();
      form.append('image', file);
      if (folderId)   form.append('folder_id',   folderId);
      if (categoryId) form.append('category_id', categoryId);
      return uploadImage(form, (e) => {
        if (e.lengthComputable) setProgress(Math.round((e.loaded / e.total) * 100));
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['images'] });
      qc.invalidateQueries({ queryKey: ['me'] });
      setProgress(null);
      setError('');
    },
    onError: (err) => {
      setError(err.response?.data?.error || 'Upload failed.');
      setProgress(null);
    },
  });

  const urlMutation = useMutation({
    mutationFn: () => saveFromUrl({
      url:         urlValue.trim(),
      name:        urlName.trim() || undefined,
      folder_id:   folderId   || undefined,
      category_id: categoryId || undefined,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['images'] });
      qc.invalidateQueries({ queryKey: ['me'] });
      setUrlValue('');
      setUrlName('');
      setError('');
      setUrlLoading(false);
    },
    onError: (err) => {
      setError(err.response?.data?.error || 'Could not save image from URL.');
      setUrlLoading(false);
    },
  });

  function handleFiles(files) {
    setError('');
    for (const file of files) {
      if (!file.type.startsWith('image/') && file.type !== 'application/pdf' && file.type !== 'application/epub+zip') continue;
      fileMutation.mutate(file);
    }
  }

  function onDrop(e) {
    e.preventDefault();
    setDragging(false);
    handleFiles([...e.dataTransfer.files]);
  }

  function handleUrlSubmit(e) {
    e.preventDefault();
    setError('');
    if (!urlValue.trim()) return;
    setUrlLoading(true);
    urlMutation.mutate();
  }

  // ── Clipboard helpers ──────────────────────────────────────────────────────
  const uploadFromBlob = useCallback((blob) => {
    if (!blob) return;
    const ext  = blob.type === 'image/png' ? '.png' : blob.type === 'image/gif' ? '.gif' : blob.type === 'image/webp' ? '.webp' : '.jpg';
    const file = new File([blob], `clipboard${ext}`, { type: blob.type });
    setPastePreview(URL.createObjectURL(blob));
    setPasteStatus('success');
    fileMutation.mutate(file);
  }, [fileMutation]);

  /** Try navigator.clipboard.read() first (Clipboard API); fall back to a message. */
  async function readClipboard() {
    setError('');
    setPasteStatus('reading');
    setPastePreview(null);
    try {
      if (!navigator.clipboard?.read) throw new Error('API_UNAVAILABLE');
      const items = await navigator.clipboard.read();
      for (const item of items) {
        const imgType = item.types.find((t) => t.startsWith('image/'));
        if (imgType) {
          const blob = await item.getType(imgType);
          uploadFromBlob(blob);
          return;
        }
      }
      setPasteStatus('idle');
      setError('No image found in clipboard. Copy an image first, then click Read clipboard.');
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        setPasteStatus('idle');
        setError('Clipboard access was denied. Press Ctrl+V (or ⌘V) anywhere on this page instead.');
      } else if (err.message === 'API_UNAVAILABLE') {
        setPasteStatus('idle');
        setError('Clipboard API not supported in this browser. Use Ctrl+V (or ⌘V) on this page.');
      } else {
        setPasteStatus('idle');
        setError('Could not read clipboard. Try Ctrl+V (or ⌘V) on this page instead.');
      }
    }
  }

  // Prevent browser from opening files dragged anywhere on the page
  useEffect(() => {
    const prevent = (e) => e.preventDefault();
    document.addEventListener('dragover', prevent);
    document.addEventListener('drop',     prevent);
    return () => {
      document.removeEventListener('dragover', prevent);
      document.removeEventListener('drop',     prevent);
    };
  }, []);

  /** Global paste listener — only active while the Paste tab is open */
  useEffect(() => {
    if (tab !== 'paste') return;
    function onPaste(e) {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const item of items) {
        if (item.type.startsWith('image/')) {
          const blob = item.getAsFile();
          if (blob) { uploadFromBlob(blob); return; }
        }
      }
    }
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [tab, uploadFromBlob]);

  // Revoke preview URL when tab changes to avoid memory leaks
  useEffect(() => {
    if (tab !== 'paste') {
      if (pastePreview) URL.revokeObjectURL(pastePreview);
      setPastePreview(null);
      setPasteStatus('idle');
    }
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-2">
      <div className="flex rounded-lg border border-gray-700 overflow-hidden w-fit">
        <button type="button" onClick={() => { setTab('file'); setError(''); }}
          className={`px-4 py-1.5 text-sm font-medium transition-colors ${tab === 'file' ? 'bg-brand-600 text-white' : 'bg-transparent text-gray-400 hover:text-white'}`}>
          Upload file
        </button>
        <button type="button" onClick={() => { setTab('url'); setError(''); }}
          className={`px-4 py-1.5 text-sm font-medium transition-colors flex items-center gap-1.5 ${tab === 'url' ? 'bg-brand-600 text-white' : 'bg-transparent text-gray-400 hover:text-white'}`}>
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
          </svg>
          From URL
        </button>
        <button type="button" onClick={() => { setTab('paste'); setError(''); }}
          className={`px-4 py-1.5 text-sm font-medium transition-colors flex items-center gap-1.5 ${tab === 'paste' ? 'bg-brand-600 text-white' : 'bg-transparent text-gray-400 hover:text-white'}`}>
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          Paste
        </button>
      </div>

      {tab === 'file' && (
        <div role="button" tabIndex={0}
          onClick={() => inputRef.current?.click()}
          onKeyDown={(e) => e.key === 'Enter' && inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${dragging ? 'border-brand-500 bg-brand-600/10' : 'border-gray-700 hover:border-brand-600 hover:bg-brand-600/5'}`}>
          <p className="text-3xl mb-2">📤</p>
          <p className="text-gray-300 font-medium">Drop files here or click to browse</p>
          <p className="text-gray-500 text-xs mt-1">JPEG, PNG, WebP, GIF, AVIF, SVG, PDF, EPUB</p>
          {progress !== null && (
            <div className="mt-4">
              <div className="h-1.5 bg-gray-700 rounded-full overflow-hidden">
                <div className="h-full bg-brand-500 transition-all duration-200" style={{ width: `${progress}%` }} />
              </div>
              <p className="text-xs text-gray-400 mt-1">{progress}%</p>
            </div>
          )}
        </div>
      )}

      {tab === 'url' && (
        <form onSubmit={handleUrlSubmit} className="border-2 border-dashed border-gray-700 rounded-xl p-6 space-y-3">
          <div className="flex items-center gap-2 mb-1">
            <svg className="w-4 h-4 text-brand-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
            <p className="text-sm text-gray-300 font-medium">Paste an image URL from the internet</p>
          </div>
          <div className="flex gap-2">
            <input type="url" value={urlValue} onChange={(e) => setUrlValue(e.target.value)}
              placeholder="https://example.com/infographic.png" required
              className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500" />
            <button type="submit" disabled={urlLoading || !urlValue.trim()}
              className="px-4 py-2 rounded-lg bg-brand-600 hover:bg-brand-500 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium transition-colors flex items-center gap-2 whitespace-nowrap">
              {urlLoading ? (
                <><span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />Saving...</>
              ) : (
                <><svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>Save image</>
              )}
            </button>
          </div>
          <input type="text" value={urlName} onChange={(e) => setUrlName(e.target.value)}
            placeholder="Optional name (e.g. AI Types Infographic)"
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500" />
          <p className="text-xs text-gray-500">Supports direct image links (.jpg .png .webp .gif .avif .svg)</p>
        </form>
      )}

      {tab === 'paste' && (
        <div className="border-2 border-dashed border-gray-700 rounded-xl p-6 space-y-4">
          {/* Header */}
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 text-brand-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <p className="text-sm text-gray-300 font-medium">Paste from clipboard</p>
          </div>

          {/* Main paste target area */}
          <div
            onClick={readClipboard}
            className="flex flex-col items-center justify-center gap-3 py-8 rounded-lg bg-gray-800/50 border border-gray-700 cursor-pointer hover:border-brand-500 hover:bg-brand-600/5 transition-colors select-none"
          >
            {pasteStatus === 'reading' ? (
              <>
                <span className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
                <p className="text-gray-400 text-sm">Reading clipboard…</p>
              </>
            ) : pasteStatus === 'success' && pastePreview ? (
              <>
                <img src={pastePreview} alt="Clipboard preview" className="max-h-40 max-w-full rounded-lg object-contain shadow-lg" />
                <p className="text-green-400 text-sm flex items-center gap-1.5">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Uploading…
                </p>
              </>
            ) : (
              <>
                <div className="text-4xl">📋</div>
                <div className="text-center">
                  <p className="text-gray-300 font-medium text-sm">Press <kbd className="bg-gray-700 text-white text-xs px-1.5 py-0.5 rounded font-mono">Ctrl+V</kbd> anywhere on this page</p>
                  <p className="text-gray-500 text-xs mt-1">or click here to read the clipboard directly</p>
                </div>
              </>
            )}
          </div>

          {/* Upload progress (reuse fileMutation progress) */}
          {progress !== null && pasteStatus === 'success' && (
            <div>
              <div className="h-1.5 bg-gray-700 rounded-full overflow-hidden">
                <div className="h-full bg-brand-500 transition-all duration-200" style={{ width: `${progress}%` }} />
              </div>
              <p className="text-xs text-gray-400 mt-1">{progress}%</p>
            </div>
          )}

          <p className="text-xs text-gray-500">
            Works with screenshot tools, image editors, and browser copy-image.
            Copy any image to your clipboard, then press <kbd className="bg-gray-700 text-white text-xs px-1 py-0.5 rounded font-mono">Ctrl+V</kbd>.
          </p>
        </div>
      )}

      {error && (
        <p className="text-red-400 text-sm flex items-center gap-1.5">
          <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {error}
        </p>
      )}

      <input ref={inputRef} type="file" accept="image/*,application/pdf,.pdf,application/epub+zip,.epub" multiple className="hidden"
        onChange={(e) => handleFiles([...e.target.files])} />
    </div>
  );
}
